"use client";

import React, { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const testimonials = [{
  author: "DEMETRIUS RICHARD\xA0",
  quote: "These lady’s are amazing in everything they do from recommending sets to go well with your request or just helpful tips on how to keep things going with your own flowers to make them last and stay healthy and beautiful for any occasion. Love love love them! Would recommend for any special event I do.",
  position: "Pop Up Client"
}, {
  author: "CHEYENNE BRAMBELLE",
  quote: "\"The cutest flower bar set up! Perfect for all things that call for celebration! I love the variety and colors in flowers and the ability to create all the prettiest bouquets  I definitely will be booking them for all future celebrations!\"",
  position: "Birthday Client"
}, {
  author: "ASHLEY HAWBECKER",
  quote: "\xA0\"Had the chance to experience Babe Bloom Bar Co. at a bridal shower, and it was such a fun, unique touch to the event! The self-serve floral arrangement setup was not only beautiful but also super interactive—everyone loved getting to create their own bouquet.\nEverything was thoughtfully curated, from the flower selection to the overall setup. It made the whole experience feel elevated and special, without being complicated or overwhelming. It was the perfect blend of creativity and relaxation, and it gave guests something meaningful to take home.\nIf you’re planning a bridal shower or any event, I highly recommend adding this—it really made the day feel extra memorable!",
  position: "Bridal Client"
}];
export default function KindWords() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222817" id="kind-words" className="bg-background text-foreground py-24 sm:py-32 overflow-hidden relative">
      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none flex items-center justify-center">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1691064/img/sage_leaf_motif.png" alt="" className="w-full max-w-4xl object-contain" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out relative z-10 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-primary tracking-wider">
            Kind Words
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg font-default">
            Discover the magic our mobile flower bar brings to every luxury celebration and special moment.
          </p>
        </div>

        <div className="max-w-5xl mx-auto relative px-12 md:px-16">
          <Carousel opts={{
          align: "center",
          loop: true,
          duration: 60
        }} className="w-full">
            <CarouselContent>
              {testimonials.map((testimonial, i) => <CarouselItem key={i} data-index={i} className="md:basis-full lg:basis-full">
                  <div className="p-2 md:p-6 h-full">
                    <Card className="text-card-foreground gap-6 border-border backdrop-blur-xl shadow-xl h-full flex flex-col justify-center items-center p-8 md:p-12 text-center rounded-xl transition-all duration-500 hover:shadow-2xl bg-background">
                      <CardContent className="p-0 flex flex-col items-center gap-8">
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, starIndex) => <Star key={starIndex} data-index={starIndex} className="lucide lucide-star size-6 fill-secondary text-primary" strokeWidth={1.5} />)}
                        </div>
                        
                        <blockquote className="space-y-2">
                          <p className="font-serif italic text-2xl md:text-3xl lg:text-4xl leading-relaxed text-muted-foreground">
                            "{testimonial.quote}"
                          </p>
                        </blockquote>
                        
                        <div className="flex flex-col items-center gap-1 mt-4">
                          <cite className="font-default font-bold text-lg not-italic text-primary uppercase tracking-widest">
                            {testimonial.author}
                          </cite>
                          <span className="text-sm font-medium tracking-wide text-primary">
                            {testimonial.position}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <CarouselPrevious className="items-center justify-center gap-2 whitespace-nowrap text-sm font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive shadow-xs dark:bg-input/30 dark:border-input dark:hover:bg-input/50 absolute top-1/2 -translate-y-1/2 flex size-12 border-secondary hover:bg-secondary hover:text-secondary-foreground border-[1px] bg-transparent -left-4 md:-left-12 transition-all duration-300 rounded-md text-primary" />
            <CarouselNext className="items-center justify-center gap-2 whitespace-nowrap text-sm font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive shadow-xs dark:bg-input/30 dark:border-input dark:hover:bg-input/50 absolute top-1/2 -translate-y-1/2 flex size-12 border-secondary hover:bg-secondary hover:text-secondary-foreground border-[1px] bg-transparent -right-4 md:-right-12 transition-all duration-300 rounded-md text-primary" />
          </Carousel>
        </div>
      </div>
    </section>;
}